/**
 * External Exercise Service
 *
 * Service for managing external exercise operations including CRUD operations,
 * and filtering. Extends the base
 * CrudService to provide exercise-specific functionality with proper
 * error handling.
 *
 * @module services/external-exercise
 */

import { apiClient } from './api/client';
import { rethrowTyped } from './api/errors';
import { CrudService } from './crud.service';
import { customExerciseService } from './custom-exercise.service';
import { selectizeService } from './selectize.service';

import type { QueryParams } from '../types/api/query.types';
import type { CreatedApiResponse } from '../types/api.types';
import type { ID } from '../types/common/id.types';
import type { ExternalExercise } from '../types/domain/exercise.types';

// =============================================================================
// THIRD PARTY EXERCISE SERVICE CLASS
// =============================================================================

/**
 * ExternalExerciseService - Handles external exercise database operations
 * Manages exercises from third-party exercise databases with full CRUD capabilities
 */
export class ExternalExerciseService extends CrudService<ExternalExercise> {
  constructor(cacheTTL?: number) {
    super({
      baseUrl: '/external/exercise',
      enableCaching: true,
      cacheTTL: cacheTTL || 5 * 60 * 1000, // 5 minutes default
    });
  }

  /**
   * Search external exercises with advanced filtering
   *
   * @param params - Search and filter parameters
   * @returns Promise resolving external exercise list
   */
  public override async getAll(
    params: QueryParams
  ): Promise<ExternalExercise[]> {
    try {
      return await super.getAll(params);
    } catch (error) {
      console.error('Failed to search external exercises:', error);
      rethrowTyped(
        error,
        'Unable to search external exercises. Please try again.'
      );
    }
  }

  /**
   * Imports an external exercise into the user's collection by its GUID
   *
   * @param id - External exercise GUID
   */
  public async import(id: ID): Promise<void> {
    try {
      await apiClient.post<CreatedApiResponse>(`${this.baseUrl}/${id}/import`);
      // The import writes to the user's custom library, whose lists are cached
      // by a different service instance — invalidate them so the next library
      // read refetches instead of serving the pre-import page. Selectize
      // options may also gain body parts / muscles (mirrors createExercise /
      // copyExercise).
      customExerciseService.invalidateListCache();
      selectizeService.clearCache();
    } catch (error) {
      console.error('Failed to import external exercise:', error);
      rethrowTyped(
        error,
        'Unable to import external exercise. Please try again.'
      );
    }
  }
}

// =============================================================================
// SERVICE INSTANCE
// =============================================================================

/**
 * Default workout service instance
 * Pre-configured and ready to use throughout the application
 */
export const externalExerciseService = new ExternalExerciseService();
